import { graphql } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

import ArticlesHorizontalList from '../../components/article/articles-horizontal-list';
import FaqMini from '../../components/faq/mini';
import Framework from '../../components/framework';
import ExpandableText from '../../components/framework/expandeble/expandable-text';
import HeroImg from '../../components/framework/hero-img';
import SectionHeader from '../../components/framework/sections/section-header';
import Slider from '../../components/framework/slider';
import Video from '../../components/framework/video';
import HowItWorks from '../../components/how-it-works/how-it-works';
import PrivacyComponent from '../../components/information/privacy_component';
import Trustpilot from '../../components/script/trustpilot';
import ProductList from '../../components/test/product-list';
import Trans from '../../components/translation/trans';
import useSiteMetadata from '../../hooks/use_site_metadata';
import useTranslation from '../../hooks/use_translation';
import largeCard from '../../images/about/about-marker.jpg';
import imgHero from '../../images/examination/ecg/ecg.jpg';

const ExaminationECGPage = ({ location }) => {
	const siteMetadata = useSiteMetadata();
	const { t } = useTranslation('examination_ecg');

	return (
		<Framework
			description={t('meta_description')}
			location={location}
			tags={t('meta_keywords')}
			title={t('title')}
		>
			<HeroImg imgSrc={imgHero} theme="dark" title={t('t1')}>
				<Trustpilot className="mt-3 mt-lg-5" theme="dark" type="starter" />
			</HeroImg>

			<section className=" bg-gray-100 py-5" id="rontgen-product-list">
				<div className="container">
					<div className="row">
						<div className="col-xl-6">
							<div className="bg-white rounded-4 p-lg-5 p-3 d-flex align-items-center justify-content-center flex-column">
								<p>{t('t2')}</p>
								<StaticImage
									alt=""
									className="img-fluid   mb-0 px-3 px-md-2 mb-lg-0 rounded-4"
									loading="lazy"
									placeholder="blurred"
									quality={100}
									src="../../images/examination/ecg/philips_logo.jpg"
									style={{ maxWidth: '200px' }}
								/>
							</div>
						</div>
						<div className="col-xl-6 mt-3 mt-lg-0">
							<div
								className={`position-relative bg-white rounded-4 p-lg-5 p-3`}
							>
								<ExpandableText maxHeight={500}>
									<ProductList
										exclude_groups={siteMetadata.config.business_tests_groups}
										grouping={false}
										include_products={[
											process.env.NODE_ENV == 'development' ? 32 : 291,
										]}
										tracking={{
											item_list_name: 'ECG products on ECG examination page',
											source: 'related',
										}}
										variant="slim"
									/>
								</ExpandableText>
							</div>
						</div>
					</div>
				</div>
			</section>
			<section className="bg-gray-100 py-5" id="ecg-about">
				<div className="container">
					<div className="row align-items-center justify-content-between">
						<div className="col-md-6 order-1 order-md-2">
							<div className="mb-3 mb-md-0">
								<StaticImage
									alt=""
									className="img-fluid img-square  mb-0 px-3 px-md-2 mb-lg-0 rounded-4"
									src="../../images/examination/ecg/ekg-kvinna.jpg"
								/>
							</div>
						</div>
						<div className="col-xl-6 col-md-5 order-2 order-md-1">
							<div className="img-fluid img-square  mb-0 position-relative  mb-lg-0 rounded-4 bg-primary text-white p-5 d-flex align-items-center justify-content-center overflow-hidden">
								<p
									className="lead position-absolute top-0 start-0 p-lg-5 p-4"
									style={{ zIndex: 6 }}
								>
									{t('t3')}
								</p>
								<Video
									variant="square"
									video_mp4={
										'https://cdn.testmottagningen.se/assets/2025/04/28/langtids-ekg-bakgrundsvideo-mp4-784.mp4'
									}
									video_webm={
										'https://cdn.testmottagningen.se/assets/2025/04/28/langtids-ekg-bakgrundsvideo-webm-785.webm'
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
			<section className="bg-gray-100 pb-5 pt-3" id="ecg-about">
				<div className="container bg-white rounded-4 o-hidden pt-3">
					<div className="row align-items-center justify-content-between">
						<div className="col-md-4 order-2 order-md-1 px-0">
							<StaticImage
								alt=""
								className="img-fluid "
								src="../../images/doctors/anders_phd.jpg"
							/>
						</div>
						<div className="col-xl-6 col-md-5 order-1 order-md-2 p-3 p-lg-0">
							<h4 className="sm fw-700 mb-2">{t('t9')}</h4>
							<p className="lead">{t('t10')}</p>
						</div>
					</div>
				</div>
			</section>

			<section className="mt-5 mb-2" id="business-how-it-works">
				<div className="container">
					<HowItWorks type="ecg" />
				</div>
			</section>
			<div className="container mb-5">
				<ProductList
					exclude_groups={[...siteMetadata.config.business_tests_groups]}
					grouping={false}
					include_products={[process.env.NODE_ENV == 'development' ? 32 : 291]}
					limit={1}
					sort={{ key: 'position', order: 'desc' }}
					tracking={{
						item_list_name: 'ECG products on ECG examination page',
						source: 'related',
					}}
					variant="featured"
				/>
			</div>
			<section className="container-fluid px-0 px-lg-5 d-flex align-items-center justify-content-center">
				<div
					className="position-relative  mb-0  mb-lg-0 rounded-4 text-white card-lg   overflow-hidden "
					style={{ background: '#050915', maxWidth: '2000px' }}
				>
					<p
						className=" position-absolute top-0 start-0 p-lg-5 p-4"
						style={{ maxWidth: '30rem' }}
					>
						{t('t4')}
					</p>

					<img
						alt={t('t4')}
						className="img-fluid"
						loading="lazy"
						src={largeCard}
					/>
				</div>
			</section>

			<section className="section">
				<div className="container ">
					<div className="row mb-3 justify-content-between">
						<div className="col-lg-5">
							<SectionHeader
								excerpt={<Trans i18nKey="t6" t={t} />}
								headingType="h3"
								title={t('t5')}
								to="/support/"
							/>
						</div>
						<div className="col-lg-6">
							<FaqMini
								include_groups={siteMetadata.config.support_product_groups.ecg}
							/>
						</div>
					</div>
				</div>
			</section>

			<section className="">
				<PrivacyComponent />
			</section>
			<section className="section">
				<div className="container ">
					<SectionHeader headingType="h3" hideButton title={t('t8')} />

					<Slider>
						<ArticlesHorizontalList
							include_diagnostic_types={['ecg']}
							limit={10}
						/>
					</Slider>
				</div>
			</section>
		</Framework>
	);
};

ExaminationECGPage.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};

export default ExaminationECGPage;

export const query = graphql`
	query {
		locales: allLocale {
			...translationFields
		}
	}
`;
